import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { DatePipe, DecimalPipe } from '@angular/common';
import {
  FormControl,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
} from '@angular/material/table';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatError, MatFormField, MatInput, MatLabel } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { ArticleFile } from '@knust/api-interfaces';
import { ArticleFilesStore } from '../../utility/signal-stores/article-files.store';
import { AuthService } from '../../utility/services/auth.service';
import { ConfirmComponent } from '../../reusable-components/confirm/confirm.component';

// Muss zu MAX_ARTICLE_FILE_SIZE / MAX_ARTICLE_FILE_LABEL_LENGTH im article.controller passen
const MAX_FILE_SIZE = 20 * 1024 * 1024;
const MAX_LABEL_LENGTH = 255;

@Component({
  selector: 'knust-article-files',
  imports: [
    DatePipe,
    DecimalPipe,
    ReactiveFormsModule,
    MatTable,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderCellDef,
    MatHeaderRow,
    MatHeaderRowDef,
    MatCell,
    MatCellDef,
    MatRow,
    MatRowDef,
    MatButton,
    MatIconButton,
    MatIcon,
    MatFormField,
    MatInput,
    MatLabel,
    MatError,
    MatTooltip,
  ],
  providers: [ArticleFilesStore],
  templateUrl: './article-files.component.html',
  styleUrl: './article-files.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ArticleFilesComponent {
  #dialog = inject(MatDialog);
  #authService = inject(AuthService);
  store = inject(ArticleFilesStore);

  articleId = input.required<number>();

  readonly maxFileSizeInMb = MAX_FILE_SIZE / 1024 / 1024;
  displayedColumns = ['label', 'createdDate', 'size', 'actions'];

  selectedFile = signal<File | undefined>(undefined);
  fileError = signal<string | undefined>(undefined);
  labelControl = new FormControl('', {
    nonNullable: true,
    validators: [
      Validators.required,
      Validators.pattern(/\S/),
      Validators.maxLength(MAX_LABEL_LENGTH),
    ],
  });

  constructor() {
    effect(() => {
      this.store.load(this.articleId());
    });
  }

  canManageFiles(): boolean {
    return this.#authService.isAdmin() || this.#authService.isPurchasing();
  }

  selectFile(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];

    this.fileError.set(undefined);
    this.selectedFile.set(undefined);

    if (!file) {
      return;
    }

    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      this.fileError.set('Es können nur PDF-Dateien hochgeladen werden.');
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      this.fileError.set('Die Datei darf maximal ' + this.maxFileSizeInMb + ' MB groß sein.');
      return;
    }

    this.selectedFile.set(file);

    // Dateiname aus der Datei vorbelegen, sofern noch keiner eingegeben wurde
    if (!this.labelControl.value.trim()) {
      this.labelControl.setValue(file.name.replace(/\.pdf$/i, '').slice(0, MAX_LABEL_LENGTH));
    }
  }

  async upload(fileInput: HTMLInputElement) {
    const file = this.selectedFile();

    if (!file || this.labelControl.invalid) {
      return;
    }

    const uploaded = await this.store.upload(
      this.articleId(),
      file,
      this.labelControl.value.trim(),
    );

    if (uploaded) {
      this.selectedFile.set(undefined);
      this.labelControl.reset();
      fileInput.value = '';
    }
  }

  download(articleFile: ArticleFile) {
    this.store.download(this.articleId(), articleFile);
  }

  deleteFile(articleFile: ArticleFile) {
    this.#dialog
      .open(ConfirmComponent, {
        data: {
          title: 'Datei wirklich löschen?',
          // Der Dialog rendert den Text als HTML, daher wird der Dateiname maskiert
          text: 'Möchten Sie die Datei „' + escapeHtml(articleFile.label) + '“ wirklich löschen?',
          buttonColor: 'warn',
        },
      })
      .afterClosed()
      .subscribe((confirm) => {
        if (confirm) {
          this.store.remove(this.articleId(), articleFile);
        }
      });
  }
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}
